import React, { useEffect, useState, useCallback } from "react";
import { TABLE_HEADERS } from "../../constants";
import { createStyles, Theme } from "@material-ui/core/styles";
import { Uam, getRequestForUAMByStatusPaginated, getReportURL } from "@api";
import { MaintenanceBreadcrumbs } from "../maintenance/breadcrumbs";
import DataTable from "react-data-table-component";
import { Box, Grid, Button, Typography } from "@material-ui/core";
import { Search } from "@components/table/toolbar/search-filters";
import { useHistory, useLocation } from "react-router-dom";
import LinearProgress from "@material-ui/core/LinearProgress";
import { makeStyles } from "@material-ui/core/styles";
import { Form } from "@components/index";
import { REPORT_TYPES } from "../constants";

interface SelectOption {
  criteria: string;
}

type Key = keyof SelectOption;

const columns = [
  {
    name: TABLE_HEADERS.EFFECTIVE_DATE,
    selector: "effectiveDate",
    sortable: true,
  },
  {
    name: TABLE_HEADERS.REF_NUMBER,
    selector: "referenceNumber",
    sortable: true,
  },
  {
    name: TABLE_HEADERS.RF_NO,
    selector: "serviceTicketNumber",
    sortable: true,
  },
  {
    name: TABLE_HEADERS.USER,
    selector: "userName",
    sortable: true,
  },
  {
    name: TABLE_HEADERS.REQUESTOR,
    selector: "requestor",
    sortable: true,
  },
  {
    name: TABLE_HEADERS.STATUS,
    selector: "status",
    sortable: true,
  },
  {
    name: TABLE_HEADERS.NAME,
    selector: "subCategory",
    sortable: true,
  },
  {
    name: TABLE_HEADERS.ENVIRONMENT,
    selector: "environment",
    sortable: true,
  },
  {
    name: TABLE_HEADERS.ROLE,
    selector: "roleName",
    sortable: true,
  },
  {
    name: TABLE_HEADERS.REQUEST_TYPE,
    selector: "requestType",
    sortable: true,
  },
  {
    name: TABLE_HEADERS.SYSTEM_CATEGORY,
    selector: "category",
    sortable: true,
  },
  {
    name: TABLE_HEADERS.DEVICE_TYPE,
    selector: "deviceType",
    sortable: true,
  },
];

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    "& > * + *": {
      marginTop: theme.spacing(2),
    },
  },
}));

const LinearIndeterminate = () => {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <LinearProgress />
    </div>
  );
};

export const UAM: React.FC = () => {
  const history = useHistory();
  const searchquery = useLocation<{
    searchterm?: string;
    searchapprove?: boolean;
    searchinprogress?: boolean;
    searchcompleted?: boolean;
    searchrejected?: boolean;
    searchdeclined?: boolean;
    searchrfdeclined?: boolean;
    searchcriteria?: string;
  }>();

  const [isLoading, setIsloading] = useState<boolean>(true);
  const [page, setPage] = React.useState<number>(1);
  const [perPage, setPerPage] = React.useState<number>(10);
  const [total, setTotal] = React.useState<number>(0);
  const [counter, updateCounter] = React.useState(0);
  const [searchQuery, setSearchQuery] = React.useState<string>(
    searchquery.state?.searchterm || ""
  );
  const [searchCriteria, setSearchCriteria] = React.useState<string>("");
  const [uam, setUam] = useState<[]>([]);
  const [sortBy, setSortBy] = useState("" || "desc");
  const [sortItem, setSortItem] = useState("" || "dateAdded");
  const [stat_APPROVED, setAPPROVED] = useState<boolean>(
    searchquery.state?.searchapprove || false
  );
  const [stat_INPROGRESS, setINPROGRESS] = useState<boolean>(
    searchquery.state?.searchinprogress || false
  );
  const [stat_COMPLETED, setCOMPLETED] = useState<boolean>(
    searchquery.state?.searchcompleted || false
  );
  const [stat_REJECTED, setREJECTED] = useState<boolean>(
    searchquery.state?.searchrejected || false
  );
  const [stat_RFDECLINED, setRFDECLINED] = useState<boolean>(
    searchquery.state?.searchrfdeclined || false
  );
  const [stat_DECLINED, setDECLINED] = useState<boolean>(
    searchquery.state?.searchdeclined || false
  );
  const [resetPaginationPage, setResetPaginationPage] = useState<boolean>(
    false
  );

  const defaultPlacerholder =
    "Search by Ref. #, RF No, Requestor, Approver or User";
  const [selectedPlaceholder, setSelectedPlaceholder] = useState<string>(
    defaultPlacerholder
  );

  const [searchBy, setSearchBy] = useState<string>(
    searchquery.state?.searchcriteria || "all"
  );

  const form = Form.useManager<SelectOption>({
    criteria: "",
  });

  const [reportURL, setReportURL] = useState<string>("");
  const [isLoadingReportURL, setIsLoadingReportURL] = useState<boolean>(false);

  function setCriteria(criteria: string) {
    if (criteria === "") {
      setSelectedPlaceholder(defaultPlacerholder);
      setSearchBy("all");
    } else if (criteria === "Reference #") {
      setSearchBy("referenceNum");
    } else if (criteria === "RF No.") {
      setSearchBy("rfNum");
    } else if (criteria === "Requestor") {
      setSearchBy("requestor");
    } else if (criteria === "Approver") {
      setSearchBy("approver");
    } else if (criteria === "User") {
      setSearchBy("user");
    }
  }

  useEffect(() => {
    const criteria = form.values.criteria;
    setCriteria(criteria);
  }, [form.values.criteria]);

  useEffect(() => {
    if (searchquery.state?.searchcriteria) {
      setSearchBy(searchquery.state?.searchcriteria);
      if (searchquery.state?.searchcriteria !== "all") {
        form.set(Form.target.VALUE, [
          "criteria",
          searchquery.state?.searchcriteria,
        ]);
      } else {
        setSelectedPlaceholder(defaultPlacerholder);
      }
    }
    if (reportURL === "") {
      setIsLoadingReportURL(true);
      getReportURL(REPORT_TYPES.UAM_REPORT)
        .then((response) => {
          setReportURL(response);
        })
        .catch((error) => {
          throw error;
        })
        .then(() => {
          setIsLoadingReportURL(false);
        });
    }
  }, []);

  useEffect(() => {
    fetchUam();
  }, [
    counter,
    perPage,
    page,
    sortItem,
    sortBy,
    stat_APPROVED,
    stat_INPROGRESS,
    stat_COMPLETED,
    stat_REJECTED,
    stat_RFDECLINED,
    stat_DECLINED,
  ]);

  // fetch if searchQuery has cleared
  useEffect(() => {
    if (!searchQuery) {
      fetchUam();
    }
  }, [searchQuery]);

  // if status or search has changed, return to first page
  useEffect(() => {
    setPage(1);
    setResetPaginationPage(!resetPaginationPage);
    setTotal(0);
  }, [
    stat_APPROVED,
    stat_INPROGRESS,
    stat_COMPLETED,
    stat_REJECTED,
    stat_RFDECLINED,
    stat_DECLINED,
  ]);

  function fetchUam() {
    setIsloading(true);
    // clear existing data to avoid conflict with the status or search query states
    setUam([]);
    getRequestForUAMByStatusPaginated(
      page,
      perPage,
      searchQuery,
      sortItem,
      sortBy,
      stat_APPROVED,
      stat_INPROGRESS,
      stat_COMPLETED,
      stat_REJECTED,
      stat_RFDECLINED,
      stat_DECLINED,
      searchBy
    )
      .then((response: any) => {
        setUam(response.requests);
        setTotal(response.totalRequests);
        setIsloading(false);
      })
      .finally(() => {
        setIsloading(false);
      });
  }

  const onSearchUpdate = useCallback((term: string) => {
    setSearchQuery(term);
  }, []);

  const handleSearch = () => {
    fetchUam();
  };

  const clearSearchItem = () => {
    setSearchQuery("");
  };

  const handleSort = (column: any, sortDirection: any) => {
    setSortItem(column.selector);
    setSortBy(sortDirection);
  };

  let clearSearchButton = searchQuery ? (
    <>
      &nbsp;
      <Button
        variant="outlined"
        color="primary"
        disabled={isLoading}
        onClick={clearSearchItem}
      >
        Clear
      </Button>
    </>
  ) : null;

  const searchSelectOptions = [
    {
      label: "No Selection",
      value: "",
    },
    {
      label: "Reference #",
      value: "Reference #",
    },
    {
      label: "RF No.",
      value: "RF No.",
    },
    {
      label: "Requestor",
      value: "Requestor",
    },
    {
      label: "Approver",
      value: "Approver",
    },
    {
      label: "User",
      value: "User",
    },
  ];

  function onChange(name: Key, value: any): void {
    form.set(Form.target.VALUE, [name, value]);
  }

  const pageName = "UAM";
  const searchText = isLoading ? "Please wait..." : "Search";
  return (
    <Box mt={2}>
      <MaintenanceBreadcrumbs title={pageName} />
      {isLoadingReportURL ? null : (
        <a
          href={reportURL}
          target="_blank"
          rel="noopener noreferrer"
          style={{ float: "right" }}
        >
          GENERATE REPORT
        </a>
      )}
      <Grid item xs={12}>
        <Typography variant="h6">UAM</Typography>
      </Grid>
      <Grid
        container
        direction="row"
        justify="space-between"
        alignItems="flex-end"
      >
        <Form.Checkbox
          label="APPROVED"
          name="status_approved"
          onChange={(name, value) => {
            setAPPROVED(value);
          }}
          size={1}
          value={stat_APPROVED}
        />
        <Form.Checkbox
          label="APPROVAL IN-PROGRESS"
          name="status_inprogress"
          onChange={(name, value) => {
            setINPROGRESS(value);
          }}
          size={2}
          value={stat_INPROGRESS}
        />
        <Form.Checkbox
          label="COMPLETED"
          name="status_completed"
          onChange={(name, value) => {
            setCOMPLETED(value);
          }}
          size={1}
          value={stat_COMPLETED}
        />
        <Form.Checkbox
          label="REJECTED"
          name="status_rejected"
          onChange={(name, value) => {
            setREJECTED(value);
          }}
          size={1}
          value={stat_REJECTED}
        />
        <Form.Checkbox
          label="DECLINED"
          name="status_rfdeclined"
          onChange={(name, value) => {
            setRFDECLINED(value);
          }}
          size={1}
          value={stat_RFDECLINED}
        />
        <Form.Checkbox
          label="USER DECLINED ATTESTATION"
          name="status_declined"
          onChange={(name, value) => {
            setDECLINED(value);
          }}
          size={3}
          value={stat_DECLINED}
        />
      </Grid>
      <br />
      <Grid container direction="row">
        <Grid item xs={4}>
          <Search
            onSearchUpdate={onSearchUpdate}
            defaultValue={searchQuery}
            placeholder={selectedPlaceholder}
          ></Search>
        </Grid>
        &nbsp;
        <Grid item xs={1}>
          <Form>
            <Form.Dropdown
              label=""
              onChange={onChange}
              options={searchSelectOptions}
              name="criteria"
              value={form.values.criteria}
              size={12}
            />
          </Form>
        </Grid>
        &nbsp;
        <Grid item xs={6}>
          <Button
            variant="contained"
            color="primary"
            type="button"
            onClick={handleSearch}
            disabled={isLoading}
          >
            {searchText}
          </Button>
          {clearSearchButton}
        </Grid>
      </Grid>
      <br />
      <DataTable
        columns={columns}
        data={uam}
        highlightOnHover
        pagination
        paginationServer
        paginationTotalRows={total}
        paginationPerPage={perPage}
        onSort={handleSort}
        sortServer
        persistTableHead
        pointerOnHover
        onChangePage={(page) => setPage(page)}
        paginationRowsPerPageOptions={[10, 30, 50]}
        onChangeRowsPerPage={(newPerPage: number, page: number) =>
          setPerPage(newPerPage)
        }
        paginationDefaultPage={1}
        paginationResetDefaultPage={resetPaginationPage}
        data-tag="allowRowEvents"
        progressPending={isLoading}
        progressComponent={<LinearIndeterminate />}
        onRowClicked={(data: any) => {
          history.push(`/dashboard/admin/uam/requests/${data.encryptId}`, {
            readonlyAttestation: true,
            searchterm: searchQuery,
            searchcriteria: searchBy,
            searchapprove: stat_APPROVED,
            searchinprogress: stat_INPROGRESS,
            searchcompleted: stat_COMPLETED,
            searchrejected: stat_REJECTED,
            searchrfdeclined: stat_RFDECLINED,
            searchdeclined: stat_DECLINED,
          });
        }}
      />
    </Box>
  );
};
